import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { InHouseOrderService } from "../../services/inHouseOrder.service";
import { Customer, POSTable_Item } from '../../services/IInHouseOrderService';
import { CoreLogService } from "../../services/log.service";
import { WebShopService } from "../../services/webShop.service";
import { WebShopMode } from "../../services/IWebShopService";
import { BasketItemViewModel, MAX_BASKETITEM_COUNT, ScheduledForRequest } from "../../shared/models";
import { Country, IBasketExtraItem, IBasketItem, IBasketService, IMenuCourse, ITableSeat, ITableSeatItem, ITableUI } from "./basket.facade";
import { ProductService } from "../../services/product.service";

class LocalBasketItem implements IBasketItem {

  get id(): number {
    return this._model.id;
  }
  get product_id(): number {
    return this._model.product_id;
  }
  get label(): string {
    return this._model.label;
  }

  public get hasImage(): boolean {
    return this._model.hasImage;
  }
  public get imageUrl(): string {
    if (this.hasImage)
      return this._model.imageUrl
    else
      return "";
  }

  get time_set(): number {
    return this._model.time_set;
  }

  get scheduled_to_hms(): number {
    return this._model.scheduled_to_hms;
  }

  get extras(): IBasketExtraItem[] {
    return this._model.product_extras;
  }

  public get comment(): string {
    return this._model.comment;
  }

  public setcomment(commentValue: string) {
    this._model.comment = commentValue;
    this._owner.updateItem(this._model);
  }

  get count(): number {
    return this._model.count;
  }
  get price(): number {
    return this._model.price;
  }

  get servicePE(): number {
    return this._model.servicePe;
  }

  get priceWithoutExtras(): number {
    return this._model.fallbackPrices.price
  }

  /** False for a coupon line (quantity fixed at 1) or when the item-count ceiling is reached. */
  get canIncrement(): boolean {
    const me = this;
    const couponId: number | string | undefined = me._model.coupon_id;
    if (couponId !== undefined && couponId !== null && couponId !== 0) {
      return false;
    }
    return (me._model.count < MAX_BASKETITEM_COUNT);
  }

  canDecrement: boolean = true;
  canDiscard: boolean = true;
  get valid_date(): number {
    return this._model.valid_date;
  }

  get deposit(): number {
    return this._model.deposit;
  }
  get variable_price(): number {
    return this._model.variable_price;
  }

  private _use_menucourse: boolean = true; // initial ist es erlaubt
  public get use_menucourse(): boolean {
    return this._use_menucourse && this._model.default_menucourse > 0;
  }
  public get menucourse(): number {
    return this._model.menucourse;
  }
  public set menucourse(value: number) {
    this._model.menucourse = value;
    this._owner.updateItem(this._model);
  }

  public get canComment(): boolean {
    return this._owner.canComment;
  }

  public showComment: boolean = false;

  public get bonusactionId(): number {
    return this._model.bonusactionId;
  }
  public get benefitId(): number {
    return this._model.benefitId;
  }

  public get benefitProductId(): number {
    return this._model.benefitProductId;
  }

  public get benefitLossId(): number {
    return this._model.benefitLossId;
  }

  public get benefitCostPoints(): number {
    return this._model.benefitCostPoints;
  }

  public get isBenefit(): boolean {
    return this._owner.iBenefit(this.benefitLossId, this.bonusactionId, this.benefitId);
  }

  public get isWebVoucher(): boolean {
    return this._model.isWebvoucher
  }
  public get webVoucher_id(): number {
    return this._model.webvoucher_id;
  }
  public get delivery_type(): number {
    return this._model.delivery_type;
  }
  public get show_delivery_type(): boolean {
    return this._model.show_delivery_type;
  }
  public get person_count(): number {
    return this._model.person_count;
  }
  public get show_person_count(): boolean {
    return this._model.show_person_count;
  }

  public get coupon_id(): number | string | undefined {
    const me = this;
    return me._model.coupon_id;
  }

  public get coupon_code(): number | string | undefined {
    const me = this;
    return me._model.coupon_code;
  }

  constructor(
    private _owner: BasketService,
    private _model: BasketItemViewModel,
    menu_courses: IMenuCourse[],
  ) {
    const me = this;
    me.showComment = me._model.comment !== "";

    // prüfe menügang, wenn es keines von den Angebotenen ist, dann entfernen
    let item_menu_course: IMenuCourse = null;
    if (me._model.menucourse > 0) {
      item_menu_course = menu_courses.find((menu_course) => menu_course.id == me._model.menucourse);
    }
    if (!item_menu_course || me._model.default_menucourse <= 0) {
      me._model.menucourse = 0; // remove menucourse
      me._use_menucourse = false; // not allow menucourse
    } // else keeps menucourse
  }

  increment() {
    if (this.canIncrement) {
      this._owner.addCountItem(this._model);
    }
  }

  decrement() {
    if (this.canDecrement) {
      this._owner.removeCountItem(this._model);
    }
  }

  async discard() {
    if (!this.canDiscard) {
      return;
    }

    const maxIterations = 2000;
    const step = 1;
    const delayBetweenIterations = 80;
    let iterations = 0;

    const removeItems = async () => {
      await this.removeNextItems(step);
      iterations += step;
    };

    while (this._model.count > 0 && iterations < maxIterations) {
      await removeItems();
      if (iterations < maxIterations) {
        await this.delay(delayBetweenIterations);
      }
    }

    if (iterations >= maxIterations) {
      console.error("Max iterations reached");
    }
  }
  async removeNextItems(step: number) {
    const concurrentRequests = 5;
    const promises = Array.from({ length: Math.min(step, concurrentRequests) }, () => this.removeSingleItem());
    await Promise.all(promises);
  }

  removeSingleItem() {
    return this._owner.removeCountItem(this._model)
      .then(() => { })
      .catch((error) => {
        console.error("Error during removal:", error);
      });
  }
  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async removeScheduledItems(): Promise<void> {
    await this._owner.removeScheduledItems(this._model);
  }

}

class TableSeatItem implements ITableSeatItem {
  get id(): string | number {
    return this._item.id;
  }

  get label(): string {
    return this._item["name_" + InternationalisationService.locale];
  }

  get count(): number {
    return this._item.amount;
  }

  get price(): number {
    return this._item.price / 100;
  }

  constructor(private _item: POSTable_Item) { }
}

class TableSeat implements ITableSeat {
  id: string | number = "";
  nickname: string = "";
  isGuest: boolean = false;
  seat: number = 0;

  open_amount: number;
  open_price: number;
  items: ITableSeatItem[];

  constructor(_customer: Customer, _items: POSTable_Item[]) {
    if (_customer) {
      this.id = _customer.user_token;
      this.nickname = _customer.nickname;
      this.isGuest = _customer.guest;
      this.seat = _customer.place_no;
    }
    this.items = _items.map((item) => (new TableSeatItem(item)));
    this.open_price = this.items.reduce((a, b) => a + b.price * b.count, 0);
    this.open_amount = this.items.reduce((a, b) => a + b.count, 0);
  }
}

@Injectable()
export class BasketService implements IBasketService, OnDestroy {
  private _tableSubject: BehaviorSubject<ITableUI> = new BehaviorSubject<ITableUI>({ no: "", name: "", place: 0, open_price: 0, });
  get table$(): Observable<ITableUI> {
    return this._tableSubject.asObservable();
  }

  public get hasTimeSelection(): boolean {
    return this._webShopService.ordertype.settingsModel.hasTimeSelection;
  }

  private _hasScheduledItems: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get hasScheduledItems$(): Observable<boolean> {
    return this._hasScheduledItems.asObservable();
  }

  private _menucourses: IMenuCourse[] = [];
  private _menucoursesSubject: BehaviorSubject<IMenuCourse[]> = new BehaviorSubject<IMenuCourse[]>([]);
  get menucourses$(): Observable<IMenuCourse[]> {
    return this._menucoursesSubject.asObservable();
  }
  private _basketItems: LocalBasketItem[];
  private _basketItemSubject: BehaviorSubject<IBasketItem[]> = new BehaviorSubject<IBasketItem[]>([]);
  get basketItems$(): Observable<IBasketItem[]> {
    return this._basketItemSubject.asObservable();
  }

  private _tableSeatsSubject: BehaviorSubject<ITableSeat[]> = new BehaviorSubject<ITableSeat[]>(null);
  get tableSeats$(): Observable<ITableSeat[]> {
    return this._tableSeatsSubject.asObservable();
  }

  private _canTipSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get canTip$(): Observable<boolean> {
    return this._canTipSubject.asObservable();
  }

  private _useRedeemCouponSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public get useRedeemCoupon$(): Observable<boolean> {
    return this._useRedeemCouponSubject.asObservable();
  }

  public get hasAcceptedAGB(): boolean {
    return this._webShopService.hasAcceptedAGB;
  }

  public set hasAcceptedAGB(accepted: boolean) {
    this._webShopService.hasAcceptedAGB = accepted;
  }

  /** TODO Implement one by one */
  // location$: Observable<IBasketLocation>;
  // selectedDay$: Observable<IDaySlot>;
  // daySlots$: Observable<IDaySlot[]>;
  // selectedTime$: Observable<ITimeSlot>;
  // timeSlots$: Observable<ITimeSlot[]>;
  // selectedDeliveryCountry$: Observable<IDeliveryCountry>;
  // deliveryCountries$: Observable<IDeliveryCountry[]>;
  // showTippingSlider: boolean;
  // tippingPercent$: Observable<number>;
  // tippingAmount$: Observable<number>;
  // deliveryFee$: Observable<number>;
  // totalSum$: Observable<number>;
  // showMinimumDeliveryValue$: Observable<boolean>;
  // minimumDeliveryValue$: Observable<number>;
  // showFreeShippingNotice$: Observable<boolean>;
  // hasAGBaccepted$: Observable<boolean>;
  // canContinue$: Observable<boolean>;
  // allowBasketModification: boolean;
  // recommendationProducts: Observable<ProductViewModel[]>;

  /** Ob Kommentierung der Artikel erlaubt ist */
  public canComment: boolean = false;

  private _sortOptions: { prop: "id" | "label" | "menucourse" | "price", ASC: boolean } = {
    prop: "id",
    ASC: true,
  };

  /** InHouse Änderungen werden genutzt um ein erneutes Binden auszulösen, wenn notwendig */
  private _onUiDataChangedSubscriber: Subscription;
  /** Warenkorb Änderungen werden in der UI Aktualisiert */
  private _shippingCartSubscriber: Subscription;
  /** Wenn Tisch sich ändert, wird die UI entsprechend Aktualisiert */
  private _onTableChangedSubscriber: Subscription;
  /** Cache für den Status ob Bestelt werden kann ohne zu Zahlen */
  private _isInhouseWithoutPayment: boolean = false;

  constructor(
    private _webShopService: WebShopService,
    private _inHouseOrderService: InHouseOrderService,
    private _coreLogService: CoreLogService,
    private _productService: ProductService
  ) {
    const me = this;

    if (me._inHouseOrderService) {
      me._onUiDataChangedSubscriber = me._inHouseOrderService.ObserveUiDataChanged.subscribe(() => {
        me.Rebind();
      });
    }

    me.Rebind(true);
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._onTableChangedSubscriber) {
      me._onTableChangedSubscriber.unsubscribe();
    }
    if (me._shippingCartSubscriber) {
      me._shippingCartSubscriber.unsubscribe();
    }
    if (me._onUiDataChangedSubscriber) {
      me._onUiDataChangedSubscriber.unsubscribe();
    }
  }

  /** Aktualisiert den Status, ob Gezahlt wird oder nur bestellt.
   * Achtung siehe auch webshop.component.ts
   */
  private Rebind(force: boolean = false): void {
    const me = this;

    me._webShopService.getWebshopSettingsDataModel().then(async (settings) => {
      // Spätere zahlung ist bei allen Bestellarten auser InHouse erlaubt
      // Für InHouse kann es auch erzwungen werden, nur nicht für die Gruppenbestellung
      const mandatory_Payment = me._inHouseOrderService.TableData.mandatory_Payment;
      const isInhouseWithoutPayment = me._webShopService.ordertype.useYASBasket && !mandatory_Payment && (settings.order_inhouse_without_payment || me._inHouseOrderService.UiData.isGroup);
      if (force || me._isInhouseWithoutPayment != isInhouseWithoutPayment) {
        me._isInhouseWithoutPayment = isInhouseWithoutPayment;

        // Inhouse Bestllung ohne Zahlung, gibt Trinkgeld erst im Bezahlprozess (InhousePaymentOrder)
        me._canTipSubject.next(me._webShopService.isTippingEnabled() && !me._isInhouseWithoutPayment);

        me._useRedeemCouponSubject.next(await me._webShopService.isCouponReedemAllowed(false));

        // momentan werden Menügänge nur beim InHouse angeboten
        if (settings.use_menu_course && me._webShopService.getServiceMode() === WebShopMode.OrderToTable) {
          let menu_courses = settings.menu_courses.sort((l, r) => l.sortnumber - r.sortnumber);
          // nur menügänge mit sortnumber 1-3
          menu_courses = menu_courses.filter((course) => course.sortnumber > 0 && course.sortnumber < 4);
          // nur die ersten 3 menügänge
          menu_courses = menu_courses.slice(0, 3);
          me._menucourses = menu_courses;
          me._menucoursesSubject.next(me._menucourses);
        } // else keine Menügänge, ui wird sie dann nicht darstellen

        me.canComment = settings.use_item_annotation;

        // Bind tableSeats when payment without_order
        if (me._isInhouseWithoutPayment) {
          // Sync on entering to gurantee correct basket.
          // ? _bindBasketItems could be called before sync to immediatly show basket
          me._webShopService.syncYASServerBasketToLocal().then(() => {
            me._bindBasketItems();
          }).catch((error) => {
            me._coreLogService.error("BasketService.constructor", "syncYASServerBasketToLocal", error);
            // TODO show error message, retry or send to landingpage?
          });

          if (me._onTableChangedSubscriber) {
            me._onTableChangedSubscriber.unsubscribe();
          }
          me._onTableChangedSubscriber = me._inHouseOrderService.ObserveOnTableChanged.subscribe(() => {
            me._bindTableUIData();
            me._bindTableSeats();
          });
          me._bindTableUIData();
          me._bindTableSeats();
        } else {
          me._bindBasketItems();
        }
      }
    }).catch((error) => {
      me._coreLogService.error("BasketService.constructor", "getWebshopSettingsDataModel.catch", error);
    });
  }

  private async _bindTableUIData() {
    const tableUiData = this._inHouseOrderService.UiData;
    if (tableUiData) {
      this._tableSubject.next({
        no: tableUiData.table_no,
        name: tableUiData.user_nickname,
        place: tableUiData.place_no,
        open_price: tableUiData.open_price,
      });
    }
  }

  private async _bindTableSeats() {
    const me = this;
    const table = await me._inHouseOrderService.GetTable(null); // null damit kein unnötiger Abruf stattfindet
    const userToken = me._inHouseOrderService.UserToken;
    const seats = me._inHouseOrderService.GetSeatsFromTable(table);

    // new TableSeats erzeugen
    const newTableSeats: ITableSeat[] = [];
    for (const key in seats) {
      if (Object.prototype.hasOwnProperty.call(seats, key)) {
        const { customer, pos_items } = seats[key];
        if (pos_items.length > 0) {
          newTableSeats.push(new TableSeat(customer, pos_items));
        }
      }
    }

    newTableSeats.sort((left, right) => {
      if (left.id === userToken || right.id === "") {
        return -1;
      } else if (right.id === userToken || left.id === "") {
        return 1;
      } else if (left.seat < right.seat) {
        return -1;
      } else if (left.seat > right.seat) {
        return 1;
      }
      // else left.seat == right.seat // sollte aber nicht sein
      return 0;
    });

    me._tableSeatsSubject.next(newTableSeats);
  }

  private _bindBasketItems() {
    const me = this;
    if (me._shippingCartSubscriber) {
      me._shippingCartSubscriber.unsubscribe();
    }
    me._shippingCartSubscriber = me._webShopService.ShippingCartObserver.subscribe((shippingCartItems) => {
      me._basketItems = shippingCartItems.map(item => new LocalBasketItem(me, item, me._menucourses));
      const { prop, ASC } = me._sortOptions;
      me._sortBasketItems(prop, ASC);
      me._basketItemSubject.next(me._basketItems);
    });
  }

  public sortBasketItemsBy(prop: "id" | "label" | "menucourse" | "price", ASC: boolean = true) {
    const me = this;
    me._sortOptions = { prop, ASC };
    me._sortBasketItems(prop, ASC);
    me._basketItemSubject.next(me._basketItems);
  }

  private _sortBasketItems(prop: "id" | "label" | "menucourse" | "price", ASC: boolean = true) {
    const me = this;
    const reverseMultiplier = ASC ? 1 : -1;
    if (prop === "label") {
      me._basketItems.sort((a, b) => reverseMultiplier * a.label.localeCompare(b.label));
    } else {
      me._basketItems.sort((a, b) => reverseMultiplier * (a[prop] - b[prop]));
    }
  }

  public updateItem(item: BasketItemViewModel) {
    const me = this;
    me._webShopService.updateYASItem(item);
    if (!me._webShopService.ordertype.useYASBasket) {
      me._webShopService.saveToStorage();
    }
  }

  public addCountItem(item: BasketItemViewModel): Promise<void> {
    return this._webShopService.addCountItem(item);
  }

  public removeCountItem(item: BasketItemViewModel): Promise<void> {
    return this._webShopService.removeCountItem(item);
  }

  public removeScheduledItems(item: BasketItemViewModel): Promise<void> {
    return this._webShopService.removeScheduledItems(item);
  }

  /** List of scheduled items that are outside the order time */
  public async getScheduledItems(schedule_req: ScheduledForRequest): Promise<LocalBasketItem[]> {
    const me = this;
    const scheduledItems: LocalBasketItem[] = [];
    return new Promise<LocalBasketItem[]>((resolve) => {
      if (!me._basketItems || me._basketItems.length === 0) {
        me._hasScheduledItems.next(false);
        resolve(scheduledItems);
        return;
      }
      let processed = 0;
      const total = me._basketItems.length;
      me._basketItems.forEach(async (item) => {
        try {
          const product = await me._productService.GetProduct(me._webShopService.locationID, me._webShopService.sortiment, item.product_id);
          if (product && !product.isScheduledFor(schedule_req).active) {
            scheduledItems.push(item);
          }
        } catch (error) {
          me._coreLogService.error("basketService.getScheduledItems", "GetProduct failed", error);
        }
        processed++;
        if (processed === total) {
          me._hasScheduledItems.next(scheduledItems.length > 0);
          resolve(scheduledItems);
        }
      });
    });
  }

  /** Clear the "has scheduled items" flag so the checkout button becomes clickable again
   *  used after a conflict dialog is dismissed without action. */
  public clearScheduledItemsFlag(): void {
    this._hasScheduledItems.next(false);
  }

  /** Remove all scheduled items that are outside the order time */
  public async removeScheduledItemsFromBasket(basketItems: LocalBasketItem[]): Promise<void> {
    const me = this;
    for (const basketItem of basketItems) {
      await basketItem.removeScheduledItems();
    }
    me._hasScheduledItems.next(false);
  }

  async continue(): Promise<boolean> {
    if (this._webShopService.ordertype.useYASBasket) {
      // Todo either sync local basket to YASServer or check for diffrences to YASServer,
      // TODO incase of diffrences sync from YASServer and notify user that Basket has changed return false
      return true;
    } else {
      return true;
    }
  }

  public iBenefit(bonusactionId: number, benefitId: number, benefitLossId: number): boolean {
    return this._webShopService.isBenefitItem(benefitLossId, bonusactionId, benefitId);
  }

  public getDefaultCountryCode(phoneNumber?: string): string {
    const DEFAULT_COUNTRY_NAME = "Deutschland";

    // Ensure the phone number is valid before checking its prefix
    if (!phoneNumber || !phoneNumber.startsWith("+")) {
      return DEFAULT_COUNTRY_NAME;
    }
    const prefix = phoneNumber.substring(0, 3);
    const countryList = this.loadDefaultCountries();
    // Find the matching country based on the phone number prefix
    const identifiedCountry = countryList.find(country => country.code === prefix);
    return identifiedCountry ? identifiedCountry.name_de : DEFAULT_COUNTRY_NAME;
  }

  private loadDefaultCountries() {
    const defaultCountries: Country[] = [
      { code: "+49", name_de: "Deutschland", name_en: "Germany" },
      { code: "+41", name_de: "Schweiz", name_en: "Switzerland" },
      { code: "+43", name_de: "Österreich", name_en: "Austria" }
    ];
    return defaultCountries;
  }
}
